import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Eye,
  FolderKanban,
  Building2,
  Users,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MapPin,
  TrendingUp,
  BarChart3,
  Filter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGO_REGIONS = ["Maritime", "Plateaux", "Centrale", "Kara", "Savanes", "DAGL"];

export const ControleurDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [filters, setFilters] = useState({
    region: '',
    project: '',
    status: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'interventions') {
      fetchInterventions();
    }
  }, [activeTab, filters]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/controleur/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInterventions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.region) params.append('region', filters.region);
      if (filters.project) params.append('project_id', filters.project);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${API}/controleur/interventions?${params.toString()}`);
      setInterventions(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'validated':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Validée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejetée</Badge>;
      default:
        return <Badge className="bg-orange-100 text-orange-700"><AlertCircle className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="controleur-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Tableau de bord Contrôleur
            </h1>
            <p className="text-slate-500 mt-1 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Vue d'ensemble en lecture seule
            </p>
          </div>
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 px-3 py-1">
            <Eye className="w-4 h-4 mr-2" />
            Mode lecture seule
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <FolderKanban className="w-4 h-4" />
              Projets
            </TabsTrigger>
            <TabsTrigger value="consultants" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Consultants
            </TabsTrigger>
            <TabsTrigger value="interventions" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Interventions
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FolderKanban className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.overview?.total_projects || 0}</p>
                      <p className="text-sm text-slate-500">Projets ({dashboard?.overview?.active_projects || 0} actifs)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.overview?.total_consultants || 0}</p>
                      <p className="text-sm text-slate-500">Consultants</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Building2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.overview?.total_companies || 0}</p>
                      <p className="text-sm text-slate-500">PME</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.overview?.validation_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Taux de validation</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Interventions Stats */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Interventions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-3xl font-bold text-slate-900">{dashboard?.overview?.total_interventions || 0}</p>
                    <p className="text-sm text-slate-500">Total</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-3xl font-bold text-emerald-700">{dashboard?.overview?.validated_interventions || 0}</p>
                    <p className="text-sm text-emerald-600">Validées</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-700">{dashboard?.overview?.pending_interventions || 0}</p>
                    <p className="text-sm text-orange-600">En attente</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-3xl font-bold text-red-700">{dashboard?.overview?.rejected_interventions || 0}</p>
                    <p className="text-sm text-red-600">Rejetées</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-700">{dashboard?.overview?.validated_hours?.toFixed(1) || 0}h</p>
                    <p className="text-sm text-blue-600">Heures validées</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gender Statistics */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Répartition par genre des dirigeants PME
                </CardTitle>
                <CardDescription>
                  Pourcentage des PME selon le genre du contact principal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6">
                  {/* Women */}
                  <div className="text-center p-6 bg-pink-50 rounded-lg border border-pink-200">
                    <div className="w-16 h-16 mx-auto mb-3 bg-pink-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👩</span>
                    </div>
                    <p className="text-4xl font-bold text-pink-700">{dashboard?.gender_stats?.female?.percentage || 0}%</p>
                    <p className="text-sm text-pink-600 mt-1">Femmes</p>
                    <p className="text-xs text-pink-500 mt-1">{dashboard?.gender_stats?.female?.count || 0} PME</p>
                  </div>
                  {/* Men */}
                  <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👨</span>
                    </div>
                    <p className="text-4xl font-bold text-blue-700">{dashboard?.gender_stats?.male?.percentage || 0}%</p>
                    <p className="text-sm text-blue-600 mt-1">Hommes</p>
                    <p className="text-xs text-blue-500 mt-1">{dashboard?.gender_stats?.male?.count || 0} PME</p>
                  </div>
                  {/* Unknown */}
                  <div className="text-center p-6 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="w-16 h-16 mx-auto mb-3 bg-slate-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">❓</span>
                    </div>
                    <p className="text-4xl font-bold text-slate-700">{dashboard?.gender_stats?.unknown?.percentage || 0}%</p>
                    <p className="text-sm text-slate-600 mt-1">Non renseigné</p>
                    <p className="text-xs text-slate-500 mt-1">{dashboard?.gender_stats?.unknown?.count || 0} PME</p>
                  </div>
                </div>
                {/* Progress bar visualization */}
                <div className="mt-6">
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div 
                      className="bg-pink-500 transition-all" 
                      style={{ width: `${dashboard?.gender_stats?.female?.percentage || 0}%` }}
                    />
                    <div 
                      className="bg-blue-500 transition-all" 
                      style={{ width: `${dashboard?.gender_stats?.male?.percentage || 0}%` }}
                    />
                    <div 
                      className="bg-slate-300 transition-all" 
                      style={{ width: `${dashboard?.gender_stats?.unknown?.percentage || 0}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mt-2 text-slate-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-pink-500 rounded"></span> Femmes
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-blue-500 rounded"></span> Hommes
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-slate-300 rounded"></span> Non renseigné
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* By Region */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Répartition par région
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(dashboard?.by_region || {}).map(([region, stats]) => (
                    <div key={region} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-slate-900">{region}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="text-slate-600">{stats.companies} PME</p>
                        <p className="text-slate-600">{stats.interventions} interventions</p>
                        <p className="text-slate-600">{stats.hours?.toFixed(1) || 0}h</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Projet</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-center">Affectations</TableHead>
                      <TableHead className="text-center">Interventions</TableHead>
                      <TableHead className="text-center">Heures validées</TableHead>
                      <TableHead>Progression</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard?.by_project?.map((project) => {
                      const progress = project.total_interventions > 0 
                        ? (project.validated_interventions / project.total_interventions * 100) 
                        : 0;
                      return (
                        <TableRow key={project.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{project.name}</p>
                              <p className="text-xs text-slate-500">
                                {project.start_date} - {project.end_date || 'En cours'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={project.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}
                            >
                              {project.status === 'active' ? 'Actif' : 'Terminé'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{project.total_assignments}</TableCell>
                          <TableCell className="text-center">
                            <div className="text-sm">
                              <span className="text-emerald-600">{project.validated_interventions}</span>
                              <span className="text-slate-400"> / </span>
                              <span>{project.total_interventions}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{project.validated_hours?.toFixed(1)}h</TableCell>
                          <TableCell>
                            <div className="w-24">
                              <Progress value={progress} className="h-2" />
                              <p className="text-xs text-slate-500 mt-1">{progress.toFixed(0)}%</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consultants Tab */}
          <TabsContent value="consultants" className="space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Consultant</TableHead>
                      <TableHead className="text-center">Affectations</TableHead>
                      <TableHead className="text-center">Interventions</TableHead>
                      <TableHead className="text-center">En attente</TableHead>
                      <TableHead className="text-center">Heures validées</TableHead>
                      <TableHead>Taux validation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard?.by_consultant?.map((consultant) => {
                      const validationRate = consultant.total_interventions > 0 
                        ? (consultant.validated_interventions / consultant.total_interventions * 100) 
                        : 0;
                      return (
                        <TableRow key={consultant.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{consultant.name}</p>
                              <p className="text-xs text-slate-500">{consultant.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{consultant.total_assignments}</TableCell>
                          <TableCell className="text-center">{consultant.total_interventions}</TableCell>
                          <TableCell className="text-center">
                            {consultant.pending_interventions > 0 && (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700">
                                {consultant.pending_interventions}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{consultant.validated_hours?.toFixed(1)}h</TableCell>
                          <TableCell>
                            <div className="w-24">
                              <Progress value={validationRate} className="h-2" />
                              <p className="text-xs text-slate-500 mt-1">{validationRate.toFixed(0)}%</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interventions Tab */}
          <TabsContent value="interventions" className="space-y-4">
            {/* Filters */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Filter className="w-4 h-4 text-slate-500" />
                  <Select value={filters.region || "all"} onValueChange={(v) => setFilters({...filters, region: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Région" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes régions</SelectItem>
                      {TOGO_REGIONS.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous statuts</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="validated">Validées</SelectItem>
                      <SelectItem value="rejected">Rejetées</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.project || "all"} onValueChange={(v) => setFilters({...filters, project: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Projet" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous projets</SelectItem>
                      {dashboard?.by_project?.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Interventions Table */}
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Consultant</TableHead>
                      <TableHead>PME</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Durée</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {interventions.slice(0, 50).map((intervention) => (
                      <TableRow key={intervention.id}>
                        <TableCell className="text-sm">{intervention.date}</TableCell>
                        <TableCell className="font-medium">{intervention.consultant_name}</TableCell>
                        <TableCell>{intervention.sme_company_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{intervention.intervention_type}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{intervention.duration_hours}h</TableCell>
                        <TableCell>{getStatusBadge(intervention.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {interventions.length > 50 && (
                  <div className="p-4 text-center text-sm text-slate-500 border-t">
                    Affichage des 50 premières interventions sur {interventions.length}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
